/**
 * Expand Go-ish placeholders for admin email preview only.
 * Does not run on Apply — saved HTML keeps real {{.Var}} / {{ range }}.
 */

import { PREVIEW_MOCK_ORDER } from "../constants/previewMockOrder";

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function formatPrice(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0.00";
    return n.toFixed(2);
}

function getPath(obj, path) {
    const parts = String(path || "")
        .replace(/^\./, "")
        .split(".")
        .filter(Boolean);
    let cur = obj;
    for (const part of parts) {
        if (cur == null) return "";
        cur = cur[part];
    }
    return cur == null ? "" : cur;
}

/**
 * Find `{{ range $i, $ln:= .LineItems }}…{{ end }}` with nested if/end depth.
 * @param {string} html
 */
function findLineItemsRange(html) {
    const startRe = /\{\{\s*range\s+\$i\s*,\s*\$ln\s*:=\s*\.LineItems\s*\}\}/;
    const start = startRe.exec(html);
    if (!start) return null;

    const bodyStart = start.index + start[0].length;
    const actionRe = /\{\{\s*(if|range|end)\b[^}]*\}\}/g;
    actionRe.lastIndex = bodyStart;
    let depth = 1;
    let match;
    while ((match = actionRe.exec(html)) !== null) {
        const kind = match[1];
        if (kind === "if" || kind === "range") depth += 1;
        else if (kind === "end") {
            depth -= 1;
            if (depth === 0) {
                return {
                    fullStart: start.index,
                    fullEnd: match.index + match[0].length,
                    body: html.slice(bodyStart, match.index),
                };
            }
        }
    }
    return null;
}

function resolveLineItemBody(body, item, index) {
    let out = String(body || "");

    // Separator row only after the first item.
    out = out.replace(
        /\{\{\s*if\s+gt\s+\$i\s+0\s*\}\}([\s\S]*?)\{\{\s*end\s*\}\}/g,
        (_full, inner) => (index > 0 ? inner : ""),
    );

    out = out.replace(/\{\{\s*\$ln\.([A-Za-z0-9_]+)\s*\}\}/g, (_full, key) =>
        escapeHtml(item?.[key] ?? ""),
    );

    out = out.replace(
        /\$\{\{\s*formatPrice\s+\$ln\.([A-Za-z0-9_]+)\s*\}\}/g,
        (_full, key) => `$${formatPrice(item?.[key])}`,
    );

    return out;
}

function expandLineItemsRange(html, mock) {
    const found = findLineItemsRange(html);
    if (!found) return html;

    const items = Array.isArray(mock?.LineItems) ? mock.LineItems : [];
    const rendered = items
        .map((item, index) => resolveLineItemBody(found.body, item, index))
        .join("");

    return (
        html.slice(0, found.fullStart) + rendered + html.slice(found.fullEnd)
    );
}

/** Keep fee/discount rows only when mock amount > 0. */
function resolveAmountGuards(html, mock) {
    return String(html || "").replace(
        /\{\{\s*if\s+gt\s+\.(TotalTax|TotalServiceFee|TotalDiscounts)\s+0\s*\}\}([\s\S]*?)\{\{\s*end\s*\}\}/g,
        (_full, field, inner) => {
            const amount = Number(getPath(mock, field));
            return amount > 0 ? inner : "";
        },
    );
}

function resolveFormatPriceCalls(html, mock) {
    return String(html || "").replace(
        /\$\{\{\s*formatPrice\s+(\.[A-Za-z0-9_.]+)\s*\}\}/g,
        (_full, path) => `$${formatPrice(getPath(mock, path))}`,
    );
}

function resolveDotPlaceholders(html, mock) {
    return String(html || "").replace(
        /\{\{\s*(\.[A-Za-z0-9_.]+)\s*\}\}/g,
        (_full, path) => escapeHtml(getPath(mock, path)),
    );
}

/**
 * @param {string} html already themed (+ OrderType preview branches)
 * @param {typeof PREVIEW_MOCK_ORDER} [mock]
 * @returns {string}
 */
export function resolvePreviewMockData(html, mock = PREVIEW_MOCK_ORDER) {
    if (!html) return "";
    let out = html;
    out = expandLineItemsRange(out, mock);
    out = resolveAmountGuards(out, mock);
    out = resolveFormatPriceCalls(out, mock);
    out = resolveDotPlaceholders(out, mock);
    return out;
}
